import type { Layout } from '@kioskforge/shared';

export type GridRect = { x: number; y: number; w: number; h: number };
export type PixelRect = { x: number; y: number; w: number; h: number };
export type GridCell = { col: number; row: number };
export type PixelPoint = { x: number; y: number };
export type PanelSize = { width: number; height: number };

export type GridRectCalculator = {
  readonly cols: number;
  readonly rows: number;
  readonly panel: PanelSize;
  readonly cellW: number;
  readonly cellH: number;
  rectOf(rect: GridRect): PixelRect;
  cellAt(point: PixelPoint): GridCell | null;
  contains(point: PixelPoint, rect: GridRect): boolean;
};

function assertPositiveInteger(label: string, value: number): void {
  if (!Number.isInteger(value) || value <= 0) throw new Error(`${label} must be a positive integer.`);
}

/**
 * Builds a grid → pixel calculator for one layout grid at one panel size.
 * Cells scale to the panel: a 12×8 grid on 800×480 gives ~66.67×60 px cells.
 * Returned rectangles are rounded to whole pixels so the two renderers (React
 * and the server SVG→sharp one) agree on block edges.
 */
export function createGrid(grid: { cols: number; rows: number }, panel: PanelSize): GridRectCalculator {
  assertPositiveInteger('grid.cols', grid.cols);
  assertPositiveInteger('grid.rows', grid.rows);
  assertPositiveInteger('panel.width', panel.width);
  assertPositiveInteger('panel.height', panel.height);

  const cellW = panel.width / grid.cols;
  const cellH = panel.height / grid.rows;

  const rectOf = (rect: GridRect): PixelRect => ({
    x: Math.round(rect.x * cellW),
    y: Math.round(rect.y * cellH),
    w: Math.round(rect.w * cellW),
    h: Math.round(rect.h * cellH)
  });

  const cellAt = (point: PixelPoint): GridCell | null => {
    if (point.x < 0 || point.y < 0 || point.x >= panel.width || point.y >= panel.height) return null;
    return { col: Math.floor(point.x / cellW), row: Math.floor(point.y / cellH) };
  };

  const contains = (point: PixelPoint, rect: GridRect): boolean => {
    const pixels = rectOf(rect);
    return point.x >= pixels.x && point.y >= pixels.y
      && point.x < pixels.x + pixels.w && point.y < pixels.y + pixels.h;
  };

  return { cols: grid.cols, rows: grid.rows, panel, cellW, cellH, rectOf, cellAt, contains };
}

function overlaps(a: GridRect, b: GridRect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Structural/geometric issues in a layout, as human-readable strings: missing
 * home, duplicate ids, out-of-grid rectangles and overlapping blocks. The
 * overlap rule stops a syntactically valid JSON with two blocks stacked on one
 * cell from publishing (b4/B2.9).
 */
export function layoutIssues(layout: Layout): string[] {
  const issues: string[] = [];
  const screenIds = new Set<string>();
  for (const screen of layout.screens) {
    if (screenIds.has(screen.id)) issues.push(`Duplicate screen id "${screen.id}".`);
    screenIds.add(screen.id);
  }
  if (!screenIds.has(layout.home)) issues.push(`Home screen "${layout.home}" does not exist.`);

  for (const screen of layout.screens) {
    const blockIds = new Set<string>();
    for (const block of screen.blocks) {
      if (blockIds.has(block.id)) issues.push(`Screen "${screen.id}": duplicate block id "${block.id}".`);
      blockIds.add(block.id);
      if (block.rect.x + block.rect.w > layout.grid.cols || block.rect.y + block.rect.h > layout.grid.rows) {
        issues.push(`Screen "${screen.id}": block "${block.id}" exceeds the grid.`);
      }
      if (block.rect.x < 0 || block.rect.y < 0) {
        issues.push(`Screen "${screen.id}": block "${block.id}" starts outside the grid.`);
      }
    }
    for (let i = 0; i < screen.blocks.length; i += 1) {
      for (let j = i + 1; j < screen.blocks.length; j += 1) {
        const first = screen.blocks[i];
        const second = screen.blocks[j];
        if (first && second && overlaps(first.rect, second.rect)) {
          issues.push(`Screen "${screen.id}": blocks "${first.id}" and "${second.id}" overlap.`);
        }
      }
    }
  }
  return issues;
}
